// Which tabs the per-repo workspace offers this caller (#195 + #1418).
//
// Eight tabs are unconditional — every role may READ what the agent decided, and
// each tab's own write plane is gated where it lives (`canOwnerWrite`). Secrets
// is the exception: `read:secrets` is owner-only now, so its tab leads nowhere
// a viewer/member/admin may look, and a tab whose only content is "you may not"
// is noise in a nine-tab bar. Deep links stay honest — `<RepoSecretsGate/>`
// answers a direct `/secrets` navigation with the owners-only line rather than
// a 403 box or a silent bounce to Overview, and holds the same `roleSettled`
// bar, so the bar and the panel can never disagree about who is asking.

import { canAccessSecrets } from './roles';

export type RepoTab = {
  readonly key: string;
  /** i18n key, resolved by the tab bar. */
  readonly label: string;
  /** Sub-path appended to `/repos/:full_name`. */
  readonly href: string;
};

// Issues sits directly after Overview: triage is the loop the product exists
// for, so it leads the operable tabs rather than trailing the read-only ones.
// Knowledge base sits beside Audit for the same reason inverted — both answer
// "what did the agent conclude?", one as a timeline and one as the prose it
// reasons from next time, and neither is a control surface.
const TABS: readonly RepoTab[] = [
  { key: 'overview', label: 'repo_detail.tab_overview', href: '' },
  { key: 'issues', label: 'repo_detail.tab_issues', href: '/issues' },
  { key: 'policy', label: 'repo_detail.tab_policy', href: '/policy' },
  { key: 'pending', label: 'repo_detail.tab_pending', href: '/pending' },
  { key: 'audit', label: 'repo_detail.tab_audit', href: '/audit' },
  // Arc 101 slice 06: what the REVIEWER has been doing on this repo. Beside
  // Audit for the same reason KB is — both answer "what did the agent conclude",
  // and neither is a control surface.
  { key: 'reviews', label: 'repo_detail.tab_reviews', href: '/reviews' },
  { key: 'kb', label: 'repo_detail.tab_kb', href: '/kb' },
  { key: 'settings', label: 'repo_detail.tab_settings', href: '/settings' },
  { key: 'secrets', label: 'repo_detail.tab_secrets', href: '/secrets' },
];

/**
 * The tabs to render. `roleResolved` is `roleSettled(me)` — NOT `!isLoading`:
 * the tab must stay shut both while `/v1/me` is in flight and while the answer
 * on hand is another tenant's (the org-switch placeholder) or a failure. An
 * absent role on a SETTLED read is a different thing entirely and keeps the tab
 * (a PAT sends none and holds its own scopes).
 */
export function repoTabs(
  role: string | null | undefined,
  roleResolved: boolean,
): readonly RepoTab[] {
  if (roleResolved && canAccessSecrets(role)) return TABS;
  return TABS.filter((tab) => tab.key !== 'secrets');
}
